import React, { useState, useEffect } from 'react';
import { getAllUsers, getUsersByTenant, updateUserRole, User, createInvitation, updateUser } from '../services/userService';
import { Shield, Globe, Mail, Loader2, CheckCircle, AlertCircle, UserPlus, Copy, ExternalLink, X, Sparkles, Edit2 } from 'lucide-react';
import { useFirebase } from '../contexts/FirebaseContext';

export const UserManagementDashboard: React.FC = () => {
  const { tenantId: currentTenantId, user: currentUser } = useFirebase();
  const isSuperAdmin = currentUser?.email?.toLowerCase() === 'admin@growezy.ai';
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteUserName, setInviteUserName] = useState('');
  const [inviteTenantId, setInviteTenantId] = useState(currentTenantId || '');
  const [inviteRole, setInviteRole] = useState<'admin' | 'manager' | 'employee'>('employee');
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);

  // Sync inviteTenantId when currentTenantId is retrieved
  useEffect(() => {
    if (currentTenantId) {
      setInviteTenantId(currentTenantId);
    }
  }, [currentTenantId]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = isSuperAdmin 
          ? await getAllUsers() 
          : (currentTenantId ? await getUsersByTenant(currentTenantId) : []);
        setUsers(data);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentTenantId, isSuperAdmin]);

  const handleRoleChange = async (user: User, newRole: string) => {
    try {
      await updateUserRole(user.uid, newRole);
      setUsers(users.map(u => u.uid === user.uid ? { ...u, role: newRole as any } : u));
      setStatus({ type: 'success', message: `Updated ${user.email} to ${newRole}` });
      setTimeout(() => setStatus(null), 3000);
    } catch (error) {
      console.error('Error updating role:', error);
      setStatus({ type: 'error', message: 'Failed to update user role' });
    }
  };

  const handleGenerateInvite = async () => {
    if (!inviteEmail || !inviteUserName || !inviteTenantId) return;
    setIsGenerating(true);
    try {
      const inviteCode = await createInvitation(inviteTenantId, inviteEmail, inviteUserName, inviteRole);
      const link = `https://tipvalue.com/signup?invite=${inviteCode}&tenantId=${inviteTenantId}`;
      setGeneratedLink(link);
      setStatus({ type: 'success', message: 'Invitation link generated!' });
    } catch (error) {
      console.error('Invite generation failed', error);
      setStatus({ type: 'error', message: 'Failed to generate invitation' });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleEditUser = async () => {
    if (!editingUser) return;
    try {
      await updateUser(editingUser.uid, { name: editName, email: editEmail });
      setUsers(users.map(u => u.uid === editingUser.uid ? { ...u, name: editName, email: editEmail } : u));
      setStatus({ type: 'success', message: 'User updated successfully!' });
      setEditingUser(null);
      setTimeout(() => setStatus(null), 3000);
    } catch (error) {
      console.error('Error updating user:', error);
      setStatus({ type: 'error', message: 'Failed to update user' });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopyFeedback(true);
    setTimeout(() => setCopyFeedback(false), 2000);
    setStatus({ type: 'success', message: 'Link copied to clipboard!' });
    setTimeout(() => setStatus(null), 2000);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-20 space-y-4">
        <Loader2 className="animate-spin text-blue-600" size={40} />
        <p className="text-gray-500 font-medium font-sans">Loading user directory...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 tracking-tight font-sans">User Management</h2>
          <p className="text-gray-500 mt-1 font-medium italic">
            {isSuperAdmin ? "Configure system access and roles across all tenants." : "Configure system access and roles for your team."}
          </p>
        </div>
        <button 
          onClick={() => setShowInviteModal(true)}
          className="btn-primary py-3 px-6 rounded-xl flex items-center gap-2 text-sm font-bold shadow-lg shadow-blue-500/20"
        >
          <UserPlus size={18} />
          <span>Invite New User</span>
        </button>
      </div>

      {status && (
        <div className={`p-4 rounded-2xl flex items-center gap-3 animate-in slide-in-from-top-2 duration-300 ${
          status.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'
        }`}>
          {status.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          <p className="text-sm font-bold">{status.message}</p>
        </div>
      )}

      {/* Mobile View: Card List */}
      <div className="grid grid-cols-1 gap-4 md:hidden">
        {users.map(user => (
          <div key={user.uid} className="card p-5 space-y-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center">
                  <Mail size={20} />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm truncate max-w-[200px]">{user.email}</p>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1 mt-0.5">
                    <Globe size={10} />
                    Tenant: {user.tenantId}
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-500">
                <Shield size={16} />
                <span className="text-xs font-bold uppercase tracking-wider">Access Role</span>
              </div>
              <select 
                value={user.role} 
                onChange={e => handleRoleChange(user, e.target.value)}
                className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs font-bold text-gray-700 focus:ring-2 focus:ring-blue-500/20 outline-none"
              >
                <option value="owner">Owner</option>
                <option value="manager">Manager</option>
                <option value="employee">Employee</option>
              </select>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop View: Table */}
      <div className="hidden md:block card overflow-hidden border border-gray-100 shadow-sm">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-50/50 border-b border-gray-100">
              <th className="px-8 py-5 font-bold text-gray-400 text-[10px] uppercase tracking-[0.2em]">User Account</th>
              <th className="px-8 py-5 font-bold text-gray-400 text-[10px] uppercase tracking-[0.2em]">Tenant Workspace</th>
              <th className="px-8 py-5 font-bold text-gray-400 text-[10px] uppercase tracking-[0.2em]">Permission Level</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {users.map(user => (
              <tr key={user.uid} className="group hover:bg-blue-50/30 transition-colors">
                <td className="px-8 py-5">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-100 text-gray-400 rounded-lg flex items-center justify-center group-hover:bg-blue-100 group-hover:text-blue-500 transition-colors">
                      <Mail size={14} />
                    </div>
                    <span className="font-semibold text-gray-900">{user.email}</span>
                  </div>
                </td>
                <td className="px-8 py-5">
                  <button 
                    onClick={() => {
                      setEditingUser(user);
                      setEditName(user.name || '');
                      setEditEmail(user.email);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <Edit2 size={16} />
                  </button>
                </td>
                <td className="px-8 py-5">
                  <div className="flex items-center gap-2 text-gray-600 font-mono text-xs">
                    <Globe size={14} className="text-gray-300" />
                    {user.tenantId}
                  </div>
                </td>
                <td className="px-8 py-5">
                  <div className="flex items-center gap-3">
                    <Shield size={16} className={`${
                      user.role === 'admin' ? 'text-purple-500' : 
                      user.role === 'manager' ? 'text-blue-500' : 'text-gray-400'
                    }`} />
                    <select 
                      value={user.role} 
                      onChange={e => handleRoleChange(user, e.target.value)}
                      className="bg-transparent border-none p-0 font-bold text-gray-700 focus:ring-0 cursor-pointer hover:text-blue-600 transition-colors"
                    >
                      <option value="owner">Owner (Executive)</option>
                      <option value="manager">Manager (GM / Regional)</option>
                      <option value="employee">Employee</option>
                    </select>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Edit Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-lg rounded-[2rem] shadow-2xl p-8 space-y-6 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-black text-gray-900 uppercase">Edit User</h3>
              <button onClick={() => setEditingUser(null)} className="p-2 hover:bg-gray-100 rounded-full text-gray-400 transition-colors">
                <X size={20} />
              </button>
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Name</label>
              <input 
                type="text"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none transition"
              />
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Email</label>
              <input 
                type="email"
                value={editEmail}
                onChange={e => setEditEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none transition"
              />
            </div>
            <div className="flex gap-4">
              <button onClick={() => setEditingUser(null)} className="w-full py-3 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition">Cancel</button>
              <button onClick={handleEditUser} className="w-full py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition">Save Changes</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-lg rounded-[2rem] shadow-2xl overflow-hidden border border-gray-100 animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 text-blue-600 rounded-xl">
                  <UserPlus size={20} />
                </div>
                <div>
                  <h3 className="text-sm font-black text-gray-900 uppercase">Magic Invite Generator</h3>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Pre-authorize team members</p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowInviteModal(false);
                  setGeneratedLink(null);
                  setInviteEmail('');
                }} 
                className="p-2 hover:bg-gray-100 rounded-full text-gray-400 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-8 space-y-6">
              {!generatedLink ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Employee Name</label>
                    <input 
                      type="text"
                      value={inviteUserName}
                      onChange={e => setInviteUserName(e.target.value)}
                      placeholder="Jane Doe"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none transition"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Employee Email</label>
                    <input 
                      type="email"
                      value={inviteEmail}
                      onChange={e => setInviteEmail(e.target.value)}
                      placeholder="email@example.com"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none transition"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Tenant ID</label>
                      <input 
                        value={inviteTenantId}
                        onChange={e => setInviteTenantId(e.target.value)}
                        placeholder="e.g., demo-1"
                        disabled={!isSuperAdmin}
                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none transition text-sm bg-gray-50 disabled:bg-gray-100 disabled:text-gray-450 font-medium"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Assigned Role</label>
                      <select 
                        value={inviteRole}
                        onChange={e => setInviteRole(e.target.value as any)}
                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none transition text-sm font-bold text-gray-700"
                      >
                        <option value="employee">Employee (Staff Member)</option>
                        <option value="manager">Manager (General Manager / Regional Manager)</option>
                        <option value="owner">Owner (Executive Admin)</option>
                      </select>
                    </div>
                  </div>
                  <button 
                    onClick={handleGenerateInvite}
                    disabled={isGenerating || !inviteEmail || !inviteUserName || !inviteTenantId}
                    className="w-full bg-gray-900 text-white rounded-xl py-4 font-black uppercase tracking-widest hover:bg-blue-600 transition-all active:scale-95 disabled:bg-gray-200 shadow-xl shadow-gray-200 flex items-center justify-center gap-2"
                  >
                    {isGenerating ? <Loader2 className="animate-spin" size={20} /> : <Sparkles size={20} />}
                    <span>Generate Invite Link</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-6 animate-in zoom-in-95 duration-300">
                  <div className="bg-blue-50 border border-blue-100 p-6 rounded-3xl text-center space-y-4">
                    <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
                      <CheckCircle size={24} />
                    </div>
                    <div>
                      <p className="text-sm font-black text-blue-900 uppercase tracking-tight">Link Ready to Share!</p>
                      <p className="text-[11px] text-blue-600 font-medium">Send this link to <span className="font-bold underline">{inviteEmail}</span></p>
                    </div>
                    
                    <div className="flex items-center gap-2 bg-white border border-blue-200 rounded-xl p-2 pl-4">
                      <p className="text-[10px] font-mono text-gray-500 truncate flex-1">{generatedLink}</p>
                      <button 
                        onClick={() => copyToClipboard(generatedLink!)}
                        className={`p-2 rounded-lg transition-colors flex items-center justify-center ${copyFeedback ? 'bg-green-600' : 'bg-blue-600'} text-white ${copyFeedback ? '' : 'hover:bg-blue-700'}`}
                      >
                        {copyFeedback ? <CheckCircle size={16} /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl text-[10px] text-gray-400 font-bold uppercase tracking-widest text-center justify-center">
                    <ExternalLink size={12} />
                    <span>This link bypasses the Tenant ID step</span>
                  </div>

                  <button 
                    onClick={() => {
                      setGeneratedLink(null);
                      setInviteEmail('');
                    }}
                    className="w-full py-3 text-xs font-black text-gray-400 uppercase tracking-widest hover:text-gray-600"
                  >
                    Generate Another
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
